import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BarChart3, Loader2, ExternalLink, Trophy, TrendingUp, Building2, Download, ArrowUp, ArrowDown, Sparkles, Eye, FileDown } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line
} from 'recharts';
import api from '../services/api';

const TopDomains = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('all');
  const [citationsDialogOpen, setCitationsDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainCitations, setDomainCitations] = useState([]);
  const [loadingCitations, setLoadingCitations] = useState(false);
  const [sortField, setSortField] = useState('totalCitations');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const response = await api.get('/websites');
        setWebsites(response.data.websites);
      } catch (err) {
        console.error('Error fetching websites:', err);
      }
    };
    fetchWebsites();
  }, []);

  useEffect(() => {
    fetchTopDomains();
  }, [selectedWebsite]);

  const fetchTopDomains = async () => {
    try {
      setLoading(true);
      setError(null);
      const websiteParam = selectedWebsite !== 'all' ? `&websiteId=${selectedWebsite}` : '';
      const response = await api.get(`/dashboard/top-domains?limit=100${websiteParam}`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching top domains:', err);
      setError(err.response?.data?.message || 'Failed to load top domains');
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = () => '#2563eb'; // blue-600 for all domains

  const handleViewCitations = async (domain) => {
    setSelectedDomain(domain);
    setCitationsDialogOpen(true);
    setLoadingCitations(true);

    try {
      const websiteParam = selectedWebsite !== 'all' ? `?websiteId=${selectedWebsite}` : '';
      const response = await api.get(`/dashboard/domain-citations/${encodeURIComponent(domain)}${websiteParam}`);
      setDomainCitations(response.data.citations || []);
    } catch (err) {
      console.error('Error fetching citations:', err);
      setDomainCitations([]);
    } finally {
      setLoadingCitations(false);
    }
  };

  const exportDomainCitationsToCSV = () => {
    if (!domainCitations || domainCitations.length === 0) return;

    const headers = ['Domain', 'Title', 'URL', 'Snippet', 'Classification', 'Brand Mentions', 'Position', 'Website', 'Date'];

    const rows = domainCitations.map(citation => [
      citation.domain,
      `"${(citation.title || '').replace(/"/g, '""')}"`,
      citation.url,
      `"${(citation.snippet || '').replace(/"/g, '""')}"`,
      citation.brand_classification || 'N/A',
      citation.brand_mention_count || 0,
      citation.position || 'N/A',
      citation.website_name,
      new Date(citation.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDomain}-citations-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    if (!data || !data.topDomains) return;

    // CSV headers
    const headers = ['Rank', 'Domain', 'Total Citations', 'Brand Mentions', 'Avg Position', 'Growth Rate (%)', 'First Seen', 'Is New'];

    // CSV rows
    const rows = data.topDomains.map(domain => [
      domain.rank,
      domain.domain,
      domain.totalCitations,
      domain.brandMentionCitations || 0,
      domain.avgPosition || 'N/A',
      domain.growthRate || 0,
      new Date(domain.firstSeen).toLocaleDateString(),
      domain.isNew ? 'Yes' : 'No'
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leading-domains-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedDomains = () => {
    if (!data || !data.topDomains) return [];

    const sorted = [...data.topDomains].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'domain':
          aVal = a.domain || '';
          bVal = b.domain || '';
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'totalCitations':
          aVal = a.totalCitations || 0;
          bVal = b.totalCitations || 0;
          break;
        case 'uniqueCitations':
          aVal = a.uniqueCitations || 0;
          bVal = b.uniqueCitations || 0;
          break;
        case 'brandMentionCitations':
          aVal = a.brandMentionCitations || 0;
          bVal = b.brandMentionCitations || 0;
          break;
        case 'avgPosition':
          aVal = a.avgPosition || 999;
          bVal = b.avgPosition || 999;
          break;
        case 'growthRate':
          aVal = a.growthRate || 0;
          bVal = b.growthRate || 0;
          break;
        case 'firstSeen':
          aVal = new Date(a.firstSeen || 0).getTime();
          bVal = new Date(b.firstSeen || 0).getTime();
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  };

  const SortableHeader = ({ field, children, className = '' }) => {
    const isSorted = sortField === field;
    return (
      <TableHead
        className={`cursor-pointer hover:bg-muted/50 ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isSorted && (
            sortDirection === 'asc' ?
              <ArrowUp className="h-3 w-3" /> :
              <ArrowDown className="h-3 w-3" />
          )}
        </div>
      </TableHead>
    );
  };

  const Sparkline = ({ data }) => {
    if (!data || data.length === 0) return <span className="text-xs text-muted-foreground">-</span>;

    return (
      <ResponsiveContainer width={80} height={30}>
        <LineChart data={data}>
          <Line
            type="monotone"
            dataKey="count"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data || data.topDomains.length === 0) {
    return (
      <Alert>
        <AlertDescription>No citation data available yet. Add some keywords and run queries to see top domains.</AlertDescription>
      </Alert>
    );
  }

  const chartData = data.topDomains.slice(0, 20).map(domain => ({
    name: domain.domain.length > 25 ? domain.domain.substring(0, 25) + '...' : domain.domain,
    citations: domain.totalCitations,
    owned: domain.ownedCitations,
    brandMention: domain.brandMentionCitations,
    competitor: domain.competitorCitations,
    other: domain.otherCitations
  }));

  const selectedWebsiteData = websites.find(w => w.id === selectedWebsite);

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <Trophy className="h-8 w-8 text-yellow-600" />
              Leading Domains
            </h1>
            <p className="text-muted-foreground">
              {selectedWebsite === 'all'
                ? `Most frequently cited domains across all ${data.totalWebsites} tracked website${data.totalWebsites !== 1 ? 's' : ''} in your organization`
                : `Most frequently cited domains for ${selectedWebsiteData?.brandName || 'selected website'}`
              }
            </p>
          </div>
          <div className="w-64">
            <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
              <SelectTrigger>
                <SelectValue placeholder="Select website" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Websites</SelectItem>
                {websites.map((website) => (
                  <SelectItem key={website.id} value={website.id}>
                    {website.brandName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Citations</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalCitations.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all keywords and websites
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Domains</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.topDomains.length.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Total distinct domains cited
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Websites Tracked</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalWebsites}</div>
            <p className="text-xs text-muted-foreground mt-1">
              In your organization
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top 20 Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Top 20 Domains by Citation Count</CardTitle>
          <CardDescription>Ranked by total number of citations</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={100} />
              <RechartsTooltip />
              <Bar dataKey="citations" name="Total Citations">
                {chartData.map((entry, index) => {
                  const originalDomain = data.topDomains[index];
                  return <Cell key={`cell-${index}`} fill={getBarColor(originalDomain)} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>All Domains (Top 100)</CardTitle>
            <CardDescription>Most frequently cited domains with trend analysis</CardDescription>
          </div>
          <Button onClick={exportToCSV} variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Rank</TableHead>
                  <SortableHeader field="domain">Domain</SortableHeader>
                  <SortableHeader field="uniqueCitations" className="text-right">Unique</SortableHeader>
                  <SortableHeader field="totalCitations" className="text-right">Total</SortableHeader>
                  <SortableHeader field="brandMentionCitations" className="text-right">Brand Mentions</SortableHeader>
                  <SortableHeader field="avgPosition" className="text-right">Avg Position</SortableHeader>
                  <SortableHeader field="growthRate" className="text-right">Growth</SortableHeader>
                  <TableHead>Trend</TableHead>
                  <SortableHeader field="firstSeen">First Seen</SortableHeader>
                  <TableHead>Citations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getSortedDomains().map((domain, index) => (
                  <TableRow key={domain.domain}>
                    <TableCell className="font-medium">
                      {domain.rank <= 3 ? (
                        <span className="flex items-center gap-1">
                          <Trophy className={`h-4 w-4 ${
                            domain.rank === 1 ? 'text-yellow-500' :
                            domain.rank === 2 ? 'text-gray-400' :
                            'text-amber-700'
                          }`} />
                          {domain.rank}
                        </span>
                      ) : (
                        domain.rank
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        {domain.domain}
                        {domain.isNew && (
                          <Badge className="bg-green-600 text-xs px-1 py-0 h-5">
                            <Sparkles className="h-3 w-3 mr-1" />
                            New
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{domain.uniqueCitations || 0}</TableCell>
                    <TableCell className="text-right font-bold">{domain.totalCitations}</TableCell>
                    <TableCell className="text-right text-blue-600">{domain.brandMentionCitations || '-'}</TableCell>
                    <TableCell className="text-right">
                      {domain.avgPosition ? (
                        <span className="text-sm font-medium">#{domain.avgPosition}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {domain.growthRate !== null && domain.growthRate !== undefined ? (
                        <span className={`flex items-center justify-end gap-1 text-sm font-medium ${
                          domain.growthRate > 0 ? 'text-green-600' :
                          domain.growthRate < 0 ? 'text-red-600' :
                          'text-muted-foreground'
                        }`}>
                          {domain.growthRate > 0 && <ArrowUp className="h-3 w-3" />}
                          {domain.growthRate < 0 && <ArrowDown className="h-3 w-3" />}
                          {domain.growthRate}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Sparkline data={domain.citationTrend} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {domain.firstSeen ? new Date(domain.firstSeen).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewCitations(domain.domain)}
                        className="text-blue-600 hover:text-blue-700 h-8"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View All
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Citations Dialog */}
      <Dialog open={citationsDialogOpen} onOpenChange={setCitationsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>All Citations for {selectedDomain}</span>
              <Button
                onClick={exportDomainCitationsToCSV}
                variant="outline"
                size="sm"
                disabled={!domainCitations || domainCitations.length === 0}
              >
                <FileDown className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </DialogTitle>
            <DialogDescription>
              {domainCitations.length} citation{domainCitations.length !== 1 ? 's' : ''} found
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto mt-4">
            {loadingCitations ? (
              <div className="flex justify-center items-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : domainCitations.length > 0 ? (
              <div className="space-y-3">
                {domainCitations.map((citation, index) => (
                  <Card key={citation.id || index} className="p-4">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm mb-1">
                            {citation.title || 'Untitled'}
                          </h4>
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {citation.url}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Badge variant={
                            citation.brand_classification === 'owned' || citation.brand_classification === 'brand' ? 'default' :
                            citation.brand_classification === 'brand_mention' ? 'secondary' :
                            citation.brand_classification === 'competitor' ? 'destructive' :
                            'outline'
                          }>
                            {citation.brand_classification || 'Unknown'}
                          </Badge>
                          {citation.position && (
                            <Badge variant="outline">
                              Position #{citation.position}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {citation.snippet && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {citation.snippet}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Website: {citation.website_name}</span>
                        {citation.brand_mention_count > 0 && (
                          <span>Brand mentions: {citation.brand_mention_count}</span>
                        )}
                        <span>{new Date(citation.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No citations found for this domain
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TopDomains;
